import {makeAutoObservable, runInAction} from 'mobx';
import {
    IAction,
    IActionStoreValue,
    IIntegartionSettings,
    IIntegration,
    IIntegrationDeleteInfo,
    IIntegrationPreset,
    INTEGRATION_ACTION,
    INTEGRATION_CATEGORY,
    INTEGRATION_CODE,
    STRIPE_ERROR,
} from '../../../../models/IntegrationsTypes';
import IntegrationsService from '../../../../services/api/integrations-service';

import {rootContainer, TYPE} from '../../../../services/container/root';
import { WithOld } from '../../../../stores/types';
import OauthCredentialsStore from './credentials/oauth-credentials-store';
import GorgiasCredentialsStore from './credentials/gorgias-credentials-store';
import GorgiasAppCredentialsStore from './credentials/gorgias-app-credentials-store';
import ZendeskCredentialsStore from './credentials/zendesk-credentials-store';
import ShipedgeCredentialsStore from './credentials/shipedge-credentials-store';
import RefundidCredentialsStore from './refundid-credentials-store';
import ReveniCredentialsStore from './reveni-credentials-store';
import NetSuiteCredentialsStore from "./credentials/net-suite-credentials-store";
import PicqerCredentialsStore from "./credentials/picqer-credentials-store";
import TrengoCredentialsStore from "./credentials/trengo-credentials-store";
import ShipheroCredentialsStore from './credentials/shiphero-credentials-store';
import PeoplevoxCredentialsStore from './credentials/peoplevox-credentials-store';
import KlaviyoCredentialsStore from './credentials/klaviyo-credentials-store';
import ReamazeCredentialsStore from './credentials/reamaze-credentials-store';
import RiseCredentialsStore from './rise-credentials-store';
import FreshdeskCredentialsStore from './credentials/freshdesk-credentials-store';
import MintsoftCredentialsStore from "./credentials/mintsoft-credentials-store";
import AmazonMcfCredentialsStore from "./credentials/amazon-mcf-credentials-store";
import Cin7CredentialsStore from "./credentials/cin7-credentials-store";
import AimsCredentialsStore from "./credentials/aims-credentials-store";
import StripeOauthCredentialsStore from "./credentials/stripe-credentials-store";
import BrightpearlOauthCredentialsStore from "./credentials/brightpearl-credentials-store";
import RlmCredentialsStore from "./credentials/rlm-credentials-store";
import BergenCredentialsStore from "./credentials/bergen-credentials-store";
import IntegrationSettingStore from './integration-setting-store';
import WhiplashOauthCredentialsStore from "./credentials/whiplash-credentials-store";
import Apparel21CredentialsStore from "./credentials/apparel21-credentials-store";
import YotpoOauthCredentialsStore from "./credentials/yotpo-credentials-store";
import ShipmonkCredentialsStore from "./credentials/shipmonk-credentials-store";
import DeposcoCredentialsStore from './credentials/deposco-credentials-store';
import TrackstarCredentialsStore from "./credentials/trackstar-credentials-store";
import ZendeskOAuthCredentialsStore from "./credentials/zendesk-oauth-credentials-store";
import AdyenOAuthCredentialsStore from './credentials/adyen-oauth-credentials-store';
import RiseAiOAuthCredentialsStore from './credentials/rise-ai-oauth-credentials-store';
import AdyenBasicCredentialsStore from './credentials/adyen-basic-credentials-store';
import BraintreeCredentialsStore from './credentials/braintree-credentials-store';
import CustomDynamicLocationCredentialsStore from './credentials/custom-dynamic-location-credentials-store';
import CybersourceCredentialsStore from './credentials/cybersource-credentials-store';
import DeliverrCredentialsStore from './credentials/deliverr-credentials-store';
import GlobalECredentialsStore from './credentials/shipping/global-e-credentials-store';

const NO_REFUNDID_ACCOUNT_FOUND = 'NO_REFUNDID_ACCOUNT_FOUND';
export interface ICredentialsStore {
    getCredentials(isSandbox?: boolean): any;
    setIsSandbox?(isSandbox?: boolean): any;
}
interface IIntegrationChanges extends Partial<IIntegration> {
    service_code: INTEGRATION_CODE,
    service_type: INTEGRATION_CATEGORY,
};
export enum INTEGRATION_STORE_TYPE {
    REGULAR = 'regular',
    SHIPPING = 'shipping'
}
export interface IIntegrationStore {
    serviceCode: string;
    serviceType: string;
    id: number;
    label: string;
    internalName: string;
    setInternalName: (value: string) => void;
    register(): Promise<void | STRIPE_ERROR>;
    forget(): Promise<void>;
    update?(): Promise<void>;
    hasChanges?(): boolean;
    setChanges(changes: any): void;
    integrationSettingsStores?: IntegrationSettingStore[];
    description?: { title: string, description: string, isHtml: boolean };
    isAllowed?: boolean;
    isEnabled?: boolean;
    isShipping: boolean;
    setIsEnabled: (value: boolean) => void;
    isLoading: boolean;
    isSandbox: boolean;
    setIsSandbox: (value: boolean) => void;
    isSandboxModeAvailable: boolean;
    credentialsStore?: ICredentialsStore;
    shouldRegister?: boolean;
    type: INTEGRATION_STORE_TYPE;
    deleteInfo: IIntegrationDeleteInfo;
    isNew(): boolean;
}
class IntegrationStore implements IIntegrationStore {
    private _model: IIntegration = {} as IIntegration;
    private _changes: IIntegrationChanges = {} as IIntegrationChanges;
    private _isLoading: boolean = false;
    private _isNew: boolean = false;
    private _isSandbox: boolean = false;

    public get isLoading(): boolean {
        return this._isLoading;
    }

    private _integrationSettingsStores: IntegrationSettingStore[] = [];
    private _credentialsStore?: ICredentialsStore;

    public get serviceCode() { return this._changes.service_code ?? this._model.service_code; }
    public get serviceType() { return this._changes.service_type ?? this._model.service_type; }
    public get id() { return this._model.id; }
    public get isShipping() { return false; }
    public get credentialsStore() { return this._credentialsStore; }
    public get description(): { title: string, description: string, isHtml: boolean } | undefined {
        if (!this.isAllowed) {
            return {
                title: 'Warning',
                description: 'Sorry, this feature is not supported in your current subscription plan. We are saving the integration details. But, we are not passing or getting information from this application.',
                isHtml: true,
            };
        }
        return {
            title: 'About the solution',
            description: this._presetValue.description,
            isHtml: true,
        }
    }
    public get isAllowed() { return this._presetValue.is_allowed; }
    public get isEnabled() { return this._changes.is_enabled ?? this._model.is_enabled; }
    public get integrationSettingsStores(): IntegrationSettingStore[] { return this._integrationSettingsStores?.filter(store => !store.isHidden) }
    public get label() { return this._presetValue.label; }
    public get shouldRegister() { return this._model.token === NO_REFUNDID_ACCOUNT_FOUND; }
    public get type() { return INTEGRATION_STORE_TYPE.REGULAR; }
    public get deleteInfo(): IIntegrationDeleteInfo { 
        return this._model._delete_info ?? {
            can_be_deleted: true
        };
    }

    public get isSandboxModeAvailable(): boolean { return this._presetValue.is_sandbox_mode_available ?? false }
    public setIsSandbox(v: boolean) {
        this._isSandbox = v;
        this.credentialsStore?.setIsSandbox?.(v);
    }
    public get isSandbox(): boolean { return this._isSandbox; }

    public isNew(): boolean {
        return this._isNew;
    }

    private get IntegrationsService(): IntegrationsService {
        return rootContainer.get<IntegrationsService>(TYPE.IntegrationsService);
    }

    constructor(private _presetValue: IIntegrationPreset, model: IIntegration = {} as IIntegration) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.setModel(model);
        this._isNew = !this._model?.id;
    }

    public get internalName() {
        return this._changes.internal_name ?? this._model.internal_name;
    }

    public setIsEnabled(v: boolean) {
        this._changes.is_enabled = v;
    }

    public setInternalName(value: string) {
        this._changes.internal_name = value;
        this._clearUnneededChanges();
    }

    private setModel(model: IIntegration = {} as IIntegration) {
        this._model = model;
        this._integrationSettingsStores = this._presetValue.actions?.map((action: IAction) => {
            const setting = this._model?.integration_settings?.find(setting => setting.action === action.id);
            return new IntegrationSettingStore(action, setting);
        }) ?? [];
    }

    public setChanges(changes: IIntegrationChanges): void {
        this._changes = changes;
        this.createCredentialsStore();
    }

    public hasChanges(): boolean {
        for (let actionStore of this._integrationSettingsStores) {
            if (actionStore.hasChanges()) {
                return true;
            }
        }
        return Object.keys(this._changes).length > 0;
    }

    private discardChanges(): void {
        this._changes = {} as IIntegrationChanges;
        this._integrationSettingsStores.forEach(s => s.discardChanges());
    }

    /** credentials stores factory */
    private createCredentialsStore() {
        const errorMsg = `unknown integration ${this.serviceType}/${this.serviceCode}`;

        switch (this.serviceCode) {
            case INTEGRATION_CODE.GORGIAS:
                this._credentialsStore = new GorgiasCredentialsStore();
                break;
            case INTEGRATION_CODE.GORGIAS_APP:
                this._credentialsStore = new GorgiasAppCredentialsStore();
                break;
            case INTEGRATION_CODE.ZENDESK:
                this._credentialsStore = new ZendeskCredentialsStore();
                break;
            case INTEGRATION_CODE.ZENDESK_OAUTH:
                this._credentialsStore = new ZendeskOAuthCredentialsStore();
                break;
            case INTEGRATION_CODE.DELIVERR:
                this._credentialsStore = new DeliverrCredentialsStore();
                break;
            case INTEGRATION_CODE.SHIPBOB:
                this._credentialsStore = new OauthCredentialsStore(this._presetValue.credentials_params);
                break;
            case INTEGRATION_CODE.NET_SUITE:
                this._credentialsStore = new NetSuiteCredentialsStore(this._presetValue.credentials_params);
                break;
            case INTEGRATION_CODE.SHIPEDGE:
                this._credentialsStore = new ShipedgeCredentialsStore();
                break;
            case INTEGRATION_CODE.REFUNDID:
                this._credentialsStore = new RefundidCredentialsStore();
                break;
            case INTEGRATION_CODE.REVENI:
                this._credentialsStore = new ReveniCredentialsStore();
                break;
            case INTEGRATION_CODE.PICQER:
                this._credentialsStore = new PicqerCredentialsStore();
                break;
            case INTEGRATION_CODE.TRENGO:
                this._credentialsStore = new TrengoCredentialsStore();
                break;
            case INTEGRATION_CODE.SHIPHERO:
                this._credentialsStore = new ShipheroCredentialsStore();
                break;
            case INTEGRATION_CODE.PEOPLEVOX:
                this._credentialsStore = new PeoplevoxCredentialsStore();
                break;
            case INTEGRATION_CODE.KLAVIYO:
                this._credentialsStore = new KlaviyoCredentialsStore();
                break;
            case INTEGRATION_CODE.REAMAZE:
                this._credentialsStore = new ReamazeCredentialsStore();
                break;
            case INTEGRATION_CODE.RISE:
                this._credentialsStore = new RiseCredentialsStore();
                break;
            case INTEGRATION_CODE.RISE_AI_OAUTH:
                this._credentialsStore = new RiseAiOAuthCredentialsStore();
                break;
            case INTEGRATION_CODE.BRIGHTPEARL:
                this._credentialsStore = new BrightpearlOauthCredentialsStore(this._presetValue.credentials_params);
                break;
            case INTEGRATION_CODE.FRESHDESK:
                this._credentialsStore = new FreshdeskCredentialsStore();
                break;
            case INTEGRATION_CODE.MINTSOFT:
                this._credentialsStore = new MintsoftCredentialsStore();
                break;
            case INTEGRATION_CODE.AMAZON_MCF:
                this._credentialsStore = new AmazonMcfCredentialsStore(this._presetValue.credentials_params);
                break;
            case INTEGRATION_CODE.CIN7:
                this._credentialsStore = new Cin7CredentialsStore();
                break;
            case INTEGRATION_CODE.AIMS:
                this._credentialsStore = new AimsCredentialsStore();
                break;
            case INTEGRATION_CODE.RLM:
                this._credentialsStore = new RlmCredentialsStore();
                break;
            case INTEGRATION_CODE.STRIPE:
                this._credentialsStore = new StripeOauthCredentialsStore(this._presetValue);
                break;
            case INTEGRATION_CODE.BERGEN:
                this._credentialsStore = new BergenCredentialsStore();
                break;
            case INTEGRATION_CODE.WHIPLASH:
                this._credentialsStore = new WhiplashOauthCredentialsStore(this._presetValue.credentials_params);
                break;
            case INTEGRATION_CODE.APPAREL21:
                this._credentialsStore = new Apparel21CredentialsStore();
                break;
            case INTEGRATION_CODE.YOTPO:
                this._credentialsStore = new YotpoOauthCredentialsStore();
                break;
            case INTEGRATION_CODE.SHIPMONK:
                this._credentialsStore = new ShipmonkCredentialsStore();
                break;
            case INTEGRATION_CODE.DEPOSCO:
                this._credentialsStore = new DeposcoCredentialsStore();
                break;
            case INTEGRATION_CODE.TRACKSTAR:
                this._credentialsStore = new TrackstarCredentialsStore();
                break;
            case INTEGRATION_CODE.ADYEN:
                this._credentialsStore = new AdyenOAuthCredentialsStore();
                break;
            case INTEGRATION_CODE.ADYEN_BASIC:
                this._credentialsStore = new AdyenBasicCredentialsStore();
                break;
            case INTEGRATION_CODE.BRAINTREE:
                this._credentialsStore = new BraintreeCredentialsStore();
                break;
            case INTEGRATION_CODE.CUSTOM_DYNAMIC_LOCATION:
                this._credentialsStore = new CustomDynamicLocationCredentialsStore();
                break;
            case INTEGRATION_CODE.CYBERSOURCE:
                this._credentialsStore = new CybersourceCredentialsStore();
                break;
			case INTEGRATION_CODE.GLOBAL_E:
                this._credentialsStore = new GlobalECredentialsStore();
                break;
            default:
                throw new Error(errorMsg);
        }
    }

    public async register() {
        this._isLoading = true;
        this._isNew = true;
        try {
            const actions = {} as IActionStoreValue;
            // On connect set default values for actions
            this._presetValue.actions?.forEach((action: IAction) => {
                if (action.id !== INTEGRATION_ACTION.STORE_NAME_INDICATION) {
                    actions[action.id] = action.default;
                }
            });
            const credentials = await this.credentialsStore?.getCredentials(this.isSandbox);
            const integration = await this.IntegrationsService.registerIntegration(
                credentials,
                this.serviceCode,
                this.serviceType,
                actions,
                this.isSandbox,
            );
            if (integration.token === NO_REFUNDID_ACCOUNT_FOUND) {
                /** In refundid integration, if there is no account the user needs to manully sign up via their portal and then retry the connection */
                runInAction(() => {
                    this.setModel({ token: NO_REFUNDID_ACCOUNT_FOUND } as IIntegration);
                    this._isLoading = false;
                })
            } else {
                runInAction(() => {
                    this.setModel(integration);
                    this._changes = {} as IIntegrationChanges;
                    this._isLoading = false;
                });
            }
        } catch (e: any) {
            let stripeError = undefined;

            runInAction(() => {
                this._isLoading = false;

                /** special case to show Stripe specific error */
                if ((e.response?.data?.message ?? '').includes('[charges_enabled]')) {
                    console.log('TODO: show Stripe popup' ,e.message);
                    stripeError = STRIPE_ERROR.ACTIVATE_ACCOUNT;
                }
            });
            console.error(e);
            return stripeError;
        }
    }

    public async forget() {
        this._isLoading = true;
        try {
            if (this._model.id) {
                const dataWithOld: WithOld<IIntegration> = {
                    ...this._model,
                    __old: this._model,
                };
                await this.IntegrationsService.removeIntegration(dataWithOld);
            }
            runInAction(() => {
                this.setChanges({
                    service_code: this.serviceCode,
                    service_type: this.serviceType,
                });
                this._model = {} as IIntegration;
                this._isLoading = false;
            });
        } catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
            // need to throw error so on remove we catch it
            throw new Error(JSON.stringify(e));
        }
    }

    private getUpdatedModel(): IIntegration {
        const integrationSettings = this._integrationSettingsStores.map(s => s.getUpdatedModel()).filter(m => !!m) as IIntegartionSettings[];
        return {
            ...this._model,
            ...this._changes,
            integration_settings: integrationSettings,
        };
    }

    public async update(): Promise<void> {
        const updatedModel: WithOld<IIntegration> = this.getUpdatedModel();
        updatedModel.__old = this._model;
        this._isLoading = true;
        let updatedIntegration: IIntegration | undefined = undefined;
        try {
            updatedIntegration = await this.IntegrationsService.updateIntegration(updatedModel);
        } catch (e) {
            console.error(`IntegrationStore.update: ${e}`);
            runInAction(() => {
                this._isLoading = false;
            });
        }

        runInAction(() => {
            if (updatedIntegration) {
                this.setModel(updatedIntegration);
            }
            this._isLoading = false;
            this.discardChanges();
        });
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IIntegration;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

export default IntegrationStore;
